import { useEffect, useMemo, useState } from "react";
import { infoApi } from "../api/infoApi";

const DEFAULT_CANONICALS = [
  "vacancy_rate_sqm",
  "demand_rea",
  "houses_available",
  "sales_last_12_months",
];

const ALIAS_TO_CANON = {
  vacancy_rate_pct: "vacancy_rate_sqm",
  vacancy_rate: "vacancy_rate_sqm",
  demand_rea_index: "demand_rea",
  rea_demand_index: "demand_rea",
  listings_sale_houses: "houses_available",
  sales_12m: "sales_last_12_months",
  sales_12_months: "sales_last_12_months",
  sales_count_12_months: "sales_last_12_months",
};

function normalizeIndicators(list) {
  const src = Array.isArray(list) && list.length ? list : DEFAULT_CANONICALS;
  const norm = src.map(k => (ALIAS_TO_CANON[k] ?? k)?.toLowerCase());
  return Array.from(new Set(norm));
}

export default function useSuburbPanelData(city, suburb, indicators = DEFAULT_CANONICALS) {
  const [details, setDetails] = useState(null);
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const canFetch = useMemo(() => Boolean(city && suburb), [city, suburb]);
  const requested = useMemo(() => normalizeIndicators(indicators), [indicators]);

  useEffect(() => {
    let cancelled = false;

    if (!canFetch) {
      setDetails(null);
      setLatest(null);
      setError(null);
      setLoading(false);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const [d, l] = await Promise.all([
          infoApi.getSuburbLookup(city, suburb),
          infoApi.getLatestBySuburb(city, suburb, requested),
        ]);

        if (cancelled) return;

        const detailsData = d?.data ?? d;
        const latestPayload = l?.data ?? l;

        const latestMap = {};
        for (const k of requested) latestMap[k] = latestPayload?.[k] ?? null;

        setDetails(detailsData);
        setLatest(latestMap);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canFetch, city, suburb, requested]);

  return { loading, error, details, latest };
}
